import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import { RootState } from '../../../core/store/store';
import { getApiBaseUrl } from '../../../core/config/api';

interface CtaCategorieMission {
  id: number;
  nom: string;
  created_at?: string;
}

const CtaCategoriesMissionManagement: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const isEmbedded = embedded || new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('embed') === '1';

  const [categories, setCategories] = useState<CtaCategorieMission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Catégories mission : Founder (1) ou Team Advisor (15) — aligné sur la nav scénarios.
  const hasAccess = currentUser?.roles_id === 1 || currentUser?.roles_id === 15;

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const loadCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) { setError("Token d'authentification manquant"); setLoading(false); return; }
      const res = await fetch(`${getApiBaseUrl()}/api/cta/categories-mission`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      const ct = res.headers.get('content-type');
      if (!ct?.includes('application/json')) {
        setError('Le serveur a retourné une réponse invalide.');
        return;
      }
      const data = await res.json();
      if (!data.success) { setError(data.error || 'Erreur chargement'); return; }
      setCategories(Array.isArray(data.data) ? data.data : []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasAccess) return;
    loadCategories();
  }, [hasAccess]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) { setError('Veuillez saisir un nom de catégorie.'); return; }
    setError(null);
    setCreating(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/cta/categories-mission`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ nom: name }),
      });
      const data = await res.json();
      if (!data.success) { setError(data.error || 'Erreur création'); return; }
      if (data.data) setCategories((prev) => [...prev, data.data]);
      setNewName('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur réseau');
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (cat: CtaCategorieMission) => {
    setEditingId(cat.id);
    setEditingName(cat.nom);
    setError(null);
  };

  const handleSave = async (id: number) => {
    const name = editingName.trim();
    if (!name) { setError('Le nom ne peut pas être vide.'); return; }
    setError(null);
    setSaving(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/cta/categories-mission/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ nom: name }),
      });
      const data = await res.json();
      if (!data.success) { setError(data.error || 'Erreur modification'); return; }
      setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, nom: name } : c)));
      setEditingId(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer cette catégorie ? Les scénarios liés ne seront pas supprimés.')) return;
    setError(null);
    setDeletingId(id);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/cta/categories-mission/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.success) { setError(data.error || 'Erreur suppression'); return; }
      setCategories((prev) => prev.filter((c) => c.id !== id));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur réseau');
    } finally {
      setDeletingId(null);
    }
  };

  if (!hasAccess) return null;

  return (
    <div className={!isEmbedded ? 'min-h-screen relative overflow-hidden' : ''}>
      {!isEmbedded && <BackgroundImage />}
      {!isEmbedded && <ParticlesBackground />}
      {!isEmbedded && <Navbar />}
      <div className={!isEmbedded ? 'relative z-10 pt-24 pb-16' : ''}>
        <div className={!isEmbedded ? 'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8' : ''}>
          {!isEmbedded && (
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Catégories de mission</h1>
                <p className="text-slate-400 text-sm">
                  Gérez les catégories de mission utilisées dans la création d&apos;interventions CTA.
                </p>
              </div>
              <button
                onClick={() => navigate('/panel')}
                className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Retour
              </button>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {error}
            </div>
          )}

          <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 p-6 backdrop-blur-sm space-y-6">
            {/* Formulaire d'ajout */}
            <div>
              <h2 className="text-sm font-semibold text-white mb-3">Ajouter une catégorie</h2>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  placeholder='Ex : "Incendie", "Accident", "Secours à personne"...'
                  className="flex-1 rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60"
                />
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={creating}
                  className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 disabled:opacity-50 transition-colors"
                >
                  {creating ? 'Ajout...' : 'Ajouter'}
                </button>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Ces catégories seront sélectionnables dans la création de scénarios d&apos;intervention.
              </p>
            </div>

            {/* Liste */}
            <div className="border-t border-[#1e2d4d] pt-4">
              <h2 className="text-sm font-semibold text-white mb-3">
                Catégories existantes{' '}
                {!loading && <span className="text-slate-500 font-normal">({categories.length})</span>}
              </h2>

              {loading ? (
                <p className="text-sm text-slate-500">Chargement...</p>
              ) : categories.length === 0 ? (
                <p className="text-sm text-slate-500">Aucune catégorie définie. Ajoutez-en ci-dessus.</p>
              ) : (
                <ul className="divide-y divide-[#1e2d4d]">
                  {categories
                    .slice()
                    .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
                    .map((cat) => (
                      <li key={cat.id} className="py-2.5 flex items-center gap-3">
                        {editingId === cat.id ? (
                          <>
                            <input
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSave(cat.id);
                                if (e.key === 'Escape') setEditingId(null);
                              }}
                              autoFocus
                              className="flex-1 rounded border border-[#3d4fdb]/60 bg-[#0a132b] px-2 py-1 text-sm text-slate-100 focus:outline-none"
                            />
                            <button
                              onClick={() => handleSave(cat.id)}
                              disabled={saving}
                              className="rounded px-3 py-1 text-xs font-semibold bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/30 disabled:opacity-50 transition-colors"
                            >
                              {saving ? '...' : 'Enregistrer'}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="rounded px-3 py-1 text-xs font-semibold bg-slate-700/40 border border-slate-600/40 text-slate-400 hover:text-slate-200 transition-colors"
                            >
                              Annuler
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-slate-200 font-medium text-sm">{cat.nom}</span>
                            {cat.created_at && (
                              <span className="text-xs text-slate-500 hidden sm:inline">
                                {new Date(cat.created_at).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                            <span className="text-xs text-slate-600">#{cat.id}</span>
                            <button
                              onClick={() => handleEdit(cat)}
                              className="rounded px-2.5 py-1 text-xs font-semibold bg-[#3d4fdb]/10 border border-[#3d4fdb]/30 text-[#8ea0f5] hover:bg-[#3d4fdb]/20 transition-colors"
                            >
                              Modifier
                            </button>
                            <button
                              onClick={() => handleDelete(cat.id)}
                              disabled={deletingId === cat.id}
                              className="rounded px-2.5 py-1 text-xs font-semibold bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 disabled:opacity-50 transition-colors"
                            >
                              {deletingId === cat.id ? '...' : 'Supprimer'}
                            </button>
                          </>
                        )}
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CtaCategoriesMissionManagement;
